import './flow-diff.css';

import { diff } from 'deep-object-diff';
import { differenceBy, isEmpty, sortBy } from 'lodash';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { useGetScratchpadBucketQuery } from '../../../../../api/graphql/generated';
import {
  ChangeHighlighter,
  ChangeLabel,
  ChangeSection,
  ChangeSectionLabel,
  ScratchpadHistoryCompareVersionsProps,
} from '../../../../../components/scratchpad/scratchpad-history-compare';
import i18n from '../../../../../i18n';
import { getLocalizedText } from '../../../../../i18n/i18n.utils';
import { useFlowServices } from '../../hook/flow-service.hooks';
import {
  ApplicationDefaultParameterSet,
  Flow,
  FlowItem,
  FlowItemForm,
  FlowItemSharedParameterRef,
  SystemUnit,
  WidgetType,
} from '../../model/flow.types';
import { FlowItemService } from '../../service/service.types';
import { convertParameterValueToUOM } from '../../util/flow-parameter.util';

interface FlowItemsDiff {
  [index: string]: Partial<FlowItem>;
}

interface FlowConfigDiff {
  [index: string]: Partial<Record<string, unknown>>;
}

interface FlowApplicationDefaultParameterSetDiff {
  [index: string]: Partial<ApplicationDefaultParameterSet>;
}

interface FlowItemDiffRow {
  id: string;
  originalFlow?: Partial<Flow>;
  originalFlowItem?: FlowItem;
  originalFlowItemIndex?: number;
  updatedFlow?: Partial<Flow>;
  updatedFlowItem?: FlowItem;
  updatedFlowItemIndex?: number;
  delta?: Partial<FlowItemForm>;
  hasValueChanges: boolean;
  isReordered: boolean;
  isDeleted: boolean;
  isNew: boolean;
}

const FlowDiff = (
  props: ScratchpadHistoryCompareVersionsProps
): ReactElement => {
  const { originalObj, updatedObj } = props;

  const [flowItemsDelta, setFlowItemsDelta] = useState<FlowItemsDiff>();

  const { flowItemService, system } = useFlowServices();

  const originalFlow = originalObj.appData as Partial<Flow> | undefined;
  const updatedFlow = updatedObj.appData as Partial<Flow> | undefined;

  useEffect(() => {
    if (originalFlow && updatedFlow) {
      const delta = diff(
        originalFlow.items?.map(sortItemParameters) ?? {},
        updatedFlow.items?.map(sortItemParameters) ?? {}
      ) as FlowItemsDiff;

      setFlowItemsDelta(delta);
    }
  }, [originalFlow, updatedFlow]);

  const rows = useMemo((): FlowItemDiffRow[] => {
    const results: FlowItemDiffRow[] = [];

    if (flowItemsDelta) {
      const deletedItems = differenceBy(
        originalFlow?.items,
        updatedFlow?.items ?? [],
        'id'
      );

      Object.keys(flowItemsDelta).forEach((indexKey: string) => {
        const updatedItemIndex = Number(indexKey);
        const updatedItem: FlowItem | undefined = updatedFlow?.items
          ? updatedFlow?.items[updatedItemIndex]
          : undefined;

        const originalItemIdToSearch =
          updatedItem?.id ?? deletedItems.shift()?.id;

        const originalItemIndex = originalFlow?.items?.findIndex(
          (i) => i.id === originalItemIdToSearch
        );

        let originalItem: FlowItem | undefined;

        if (originalItemIndex !== undefined && originalItemIndex > -1) {
          originalItem = originalFlow?.items
            ? originalFlow?.items[originalItemIndex]
            : undefined;
        }

        let itemDelta: Partial<FlowItemForm> | undefined;

        if (originalItem && updatedItem) {
          const originalItemConverted = flowItemService.convertFlowItemToForm(
            originalItem,
            system,
            true
          );
          const updatedItemConverted = flowItemService.convertFlowItemToForm(
            updatedItem,
            system,
            true
          );
          itemDelta = diff(originalItemConverted, updatedItemConverted);
        }

        const hasValueChanges = !isEmpty(itemDelta ?? {});
        const isDeleted = updatedItem === undefined;
        const isNew = originalItem === undefined && updatedItem !== undefined;
        const isReordered =
          !isNew && !isDeleted && originalItemIndex !== updatedItemIndex;

        const row: FlowItemDiffRow = {
          id: updatedItem?.id ?? uuid(),
          delta: itemDelta,
          originalFlow: originalFlow,
          originalFlowItem: originalItem,
          originalFlowItemIndex: originalItemIndex,
          updatedFlow: updatedFlow,
          updatedFlowItem: updatedItem,
          updatedFlowItemIndex: updatedItemIndex,
          hasValueChanges,
          isDeleted,
          isNew,
          isReordered,
        };

        results.push(row);
      });
    }

    return results;
  }, [flowItemService, flowItemsDelta, originalFlow, updatedFlow, system]);

  const isOnlyReorder = useCallback(
    (row: FlowItemDiffRow): boolean =>
      row.isReordered && !row.isDeleted && !row.isNew && !row.hasValueChanges,
    []
  );

  const mostRelevantRows = useMemo(
    () =>
      sortBy(
        rows.filter((r) => !isOnlyReorder(r)),
        (r) => (r.isDeleted ? r.originalFlowItemIndex : r.updatedFlowItemIndex)
      ),
    [rows, isOnlyReorder]
  );
  const hasRelevantRows = mostRelevantRows.length > 0;

  const reorderOnlyRows = rows.filter((r) =>
    hasRelevantRows ? isOnlyReorder(r) : true
  );

  return (
    <>
      <FlowConfigDeltaComponent
        originalFlow={originalFlow}
        updatedFlow={updatedFlow}
      />
      <FlowParameterSetDeltaComponent
        originalFlow={originalFlow}
        updatedFlow={updatedFlow}
      />
      <ChangeSection>
        {mostRelevantRows.length > 0 && (
          <>
            <ChangeSectionLabel
              label={i18n.t(
                'common:scratchpad_version_items_added_changed_deleted'
              )}
            />
            {mostRelevantRows.map((r, index) => (
              <FlowItemDiffRowComponent
                key={r.id}
                flowItemService={flowItemService}
                system={system}
                index={index}
                row={r}
                rows={rows}
              />
            ))}
          </>
        )}
        {reorderOnlyRows.length > 0 && (
          <div>
            <ChangeSectionLabel
              label={i18n.t('common:scratchpad_version_items_only_reordered')}
            />
            {reorderOnlyRows.map((r, index) => (
              <FlowItemDiffRowComponent
                key={r.id}
                flowItemService={flowItemService}
                system={system}
                index={index}
                row={r}
                rows={rows}
              />
            ))}
          </div>
        )}
      </ChangeSection>
    </>
  );
};

interface FlowConfigDeltaProps {
  originalFlow?: Partial<Flow>;
  updatedFlow?: Partial<Flow>;
}

const FlowConfigDeltaComponent = (
  props: FlowConfigDeltaProps
): ReactElement => {
  const { originalFlow, updatedFlow } = props;

  const flowConfigDelta = useMemo(() => {
    return diff(
      originalFlow?.config ?? {},
      updatedFlow?.config ?? {}
    ) as FlowConfigDiff;
  }, [originalFlow?.config, updatedFlow?.config]);

  if (flowConfigDelta && !isEmpty(flowConfigDelta)) {
    const updatedStartAt =
      originalFlow?.config?.startAtId !== updatedFlow?.config?.startAtId;

    const updatedStepModeEnabled =
      originalFlow?.config?.stepModeEnabled !==
      updatedFlow?.config?.stepModeEnabled;

    let updatedStartChange: ReactElement | null = null;
    let stepModeEnabledChanged: ReactElement | null = null;
    if (updatedStartAt) {
      const originalStartAtItem = originalFlow?.items?.find(
        (i) => i.id === originalFlow?.config?.startAtId
      );
      const updatedStartAtItem = updatedFlow?.items?.find(
        (i) => i.id === updatedFlow.config?.startAtId
      );

      updatedStartChange = (
        <>
          <ChangeLabel
            label={`${i18n.t('common:scratchpad_version_start_from_changed')}:`}
          />
          <ChangeHighlighter
            oldVal={originalStartAtItem?.name}
            newVal={updatedStartAtItem?.name}
            noValueLabel={i18n.t('common:scratchpad_version_unset')}
          />
        </>
      );
    }

    if (updatedStepModeEnabled) {
      stepModeEnabledChanged = (
        <>
          <ChangeLabel
            label={`${i18n.t('common:scratchpad_version_step_mode')}:`}
          />
          <ChangeHighlighter
            oldVal={
              originalFlow?.config?.stepModeEnabled
                ? `${i18n.t('common:scratchpad_version_on')}`
                : `${i18n.t('common:scratchpad_version_off')}`
            }
            newVal={
              updatedFlow?.config?.stepModeEnabled
                ? `${i18n.t('common:scratchpad_version_on')}`
                : `${i18n.t('common:scratchpad_version_off')}`
            }
            noValueLabel={i18n.t('common:scratchpad_version_unset')}
          />
        </>
      );
    }

    return (
      <ChangeSection>
        <ChangeSectionLabel
          label={i18n.t('common:scratchpad_version_config_changed')}
        />
        {updatedStartChange}
        <br />
        {stepModeEnabledChanged}
      </ChangeSection>
    );
  }
  return <div />;
};

const FlowParameterSetDeltaComponent = (
  props: FlowConfigDeltaProps
): ReactElement => {
  const { originalFlow, updatedFlow } = props;

  const defaultParameterSetDelta = useMemo(() => {
    return diff(
      originalFlow?.defaultParameterSet ?? {},
      updatedFlow?.defaultParameterSet ?? {}
    ) as FlowApplicationDefaultParameterSetDiff;
  }, [originalFlow?.defaultParameterSet, updatedFlow?.defaultParameterSet]);

  const { data: originalQueryData } = useGetScratchpadBucketQuery({
    variables: {
      input: { id: originalFlow?.defaultParameterSet?.id as string },
    },
    skip: originalFlow?.defaultParameterSet?.id === undefined,
  });

  const { data: updatedQueryData } = useGetScratchpadBucketQuery({
    variables: {
      input: { id: updatedFlow?.defaultParameterSet?.id as string },
    },
    skip: updatedFlow?.defaultParameterSet?.id === undefined,
  });

  if (defaultParameterSetDelta && !isEmpty(defaultParameterSetDelta)) {
    const isLibraryIdUpdated =
      originalFlow?.defaultParameterSet?.id !==
      updatedFlow?.defaultParameterSet?.id;

    /**
     * We are forcing these values to string because the code to assign
     * buckets to the default settings was using the bucket type for version
     * which is "number". Typescript wasn't catching the error which allowed
     * the issue to propogate and since being fixed, we need to ensure
     * that if there is a number to string comparsion that it's handled
     * correctly.
     */
    const originalVersion = String(originalFlow?.defaultParameterSet?.version);
    const updatedVersion = String(updatedFlow?.defaultParameterSet?.version);

    const isLibraryVersionUpdated = originalVersion !== updatedVersion;

    let updatedLibrary: ReactElement | null = null;
    if (isLibraryIdUpdated) {
      const originalName = originalQueryData?.getScratchpadBucket.name;
      const updatedName = updatedQueryData?.getScratchpadBucket.name;

      updatedLibrary = (
        <>
          <ChangeLabel
            label={`${i18n.t('common:scratchpad_version_library_changed')}:`}
          />
          <ChangeHighlighter
            oldVal={originalName}
            newVal={updatedName}
            noValueLabel={i18n.t('common:scratchpad_version_unset')}
          />
        </>
      );
    } else if (isLibraryVersionUpdated) {
      updatedLibrary = (
        <>
          <ChangeLabel
            label={`${i18n.t(
              'common:scratchpad_version_library_version_changed'
            )}:`}
          />
          <ChangeHighlighter
            oldVal={originalFlow?.defaultParameterSet?.version}
            newVal={updatedFlow?.defaultParameterSet?.version}
            noValueLabel={i18n.t('common:scratchpad_version_unset')}
          />
        </>
      );
    }
    if (!isLibraryIdUpdated && !isLibraryVersionUpdated) {
      updatedLibrary = (
        <ChangeLabel
          label={i18n.t('common:scratchpad_version_settings_changed')}
        />
      );
    }

    return (
      <ChangeSection>
        <ChangeSectionLabel
          label={i18n.t('common:scratchpad_version_initial_settings_changed')}
        />
        {updatedLibrary}
      </ChangeSection>
    );
  }
  return <div />;
};

interface FlowItemDiffProps {
  flowItemService: FlowItemService;
  system: SystemUnit;
  index: number;
  row: FlowItemDiffRow;
  rows: FlowItemDiffRow[];
}

const FlowItemDiffRowComponent = (props: FlowItemDiffProps): ReactElement => {
  const {
    flowItemService,
    system,
    row: {
      originalFlow,
      originalFlowItem,
      originalFlowItemIndex,
      updatedFlow,
      updatedFlowItem,
      updatedFlowItemIndex,
      delta,
      hasValueChanges,
      isDeleted,
      isNew,
      isReordered,
    },
  } = props;

  let indexText: ReactElement = (
    <>
      {updatedFlowItemIndex !== undefined
        ? updatedFlowItemIndex + 1
        : 'Deleted'}
    </>
  );
  if (isDeleted) {
    indexText = <>{(originalFlowItemIndex ?? 0) + 1}</>;
  }
  if (isNew) {
    indexText = (
      <div className="item-name-changed">
        <span>
          {i18n.t('common:scratchpad_version_inserted_at')} {indexText}
        </span>
      </div>
    );
  } else if (isReordered) {
    indexText = (
      <div className="item-name-changed">
        <span className="item-name-old">
          {(originalFlowItemIndex ?? 0) + 1}
        </span>
        <span className="item-change-subtle">{`→`}</span>
        <span className="item-name-new">{(updatedFlowItemIndex ?? 0) + 1}</span>
      </div>
    );
  }

  let itemName: ReactElement = (
    <span className="item-name-new">{updatedFlowItem?.name}</span>
  );
  if (delta?.name) {
    itemName = (
      <div className="item-name-changed">
        <span className="item-name-old">{originalFlowItem?.name}</span>
        <span className="item-change-subtle">{`→`}</span>
        {itemName}
      </div>
    );
  } else if (isDeleted) {
    itemName = (
      <div className="item-name-changed">
        <span>
          {originalFlowItem?.name} (
          {i18n.t('common:scratchpad_version_deleted')})
        </span>
      </div>
    );
  }

  const wrapperClassList: string[] = ['diff-item-wrapper'];

  if (isDeleted) {
    wrapperClassList.push('diff-deleted-item');
  }
  if (isNew) {
    wrapperClassList.push('diff-new-item');
  }
  if (isReordered) {
    wrapperClassList.push('diff-reordered-item');
  }

  return (
    <div className={wrapperClassList.join(' ')}>
      <div className="diff-item-container">
        <div className="diff-item-header">
          {indexText}: {itemName}
        </div>
        {hasValueChanges && (
          <>
            <div className="diff-item-header-from">
              {i18n.t('common:scratchpad_version_this_version')}
            </div>
            <div className="diff-item-header-to">
              {i18n.t('common:scratchpad_version_newer_version')}
            </div>
            <div className="diff-item-changes-from">
              <ParameterChanges
                flowItemService={flowItemService}
                system={system}
                delta={delta}
                referencedFlow={originalFlow}
                referencedItem={originalFlowItem}
                fallback={<div />}
              />
            </div>
            <div className="diff-item-changes-to">
              <ParameterChanges
                flowItemService={flowItemService}
                system={system}
                delta={delta}
                referencedFlow={updatedFlow}
                referencedItem={updatedFlowItem}
                fallback={<div />}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};

interface ParameterChanges {
  flowItemService: FlowItemService;
  system: SystemUnit;
  referencedFlow?: Partial<Flow>;
  referencedItem?: FlowItem;
  delta?: Partial<FlowItemForm>;
  fallback: ReactElement;
}

const ParameterChanges = (props: ParameterChanges): ReactElement => {
  const { fallback, flowItemService, delta, referencedItem, system } = props;

  if (referencedItem) {
    const parameters = flowItemService.getParametersMetadata(
      referencedItem?.ref
    );

    let hasUpdatedRobotPosition = false;

    const changes: string[] = [];

    Object.entries(delta?.parameters ?? {}).forEach(([ref, _data]) => {
      const exists = referencedItem.parameters.find((el) => el.ref === ref);

      if (
        ref === FlowItemSharedParameterRef.WAYPOINT ||
        ref === FlowItemSharedParameterRef.QNEAR
      ) {
        hasUpdatedRobotPosition = true;
        return;
      }
      const metadata = parameters.find((el) => el.ref === ref);
      const label = metadata ? getLocalizedText(metadata.name) : ref;

      if (exists) {
        const value = exists.value;
        let convertedValue = value;
        if (metadata?.ui?.widget === WidgetType.PICKER) {
          const options = (metadata?.ui?.widgetOptions ?? []).find(
            (o) => o.value === value
          );
          convertedValue = getLocalizedText(options?.label) ?? value;
        } else if (
          value !== undefined &&
          typeof value === 'number' &&
          metadata
        ) {
          convertedValue = convertParameterValueToUOM(
            metadata?.value,
            system,
            value,
            false
          );
        }
        changes.push(`${label}: ${convertedValue}`);
      } else {
        changes.push(`${label}: ${i18n.t('common:scratchpad_version_unset')}`);
      }
    });

    return (
      <>
        {hasUpdatedRobotPosition && (
          <div className="changed-item-container">
            <span className="changed-item-text">
              {i18n.t('common:scratchpad_version_position_changed')}
            </span>
          </div>
        )}
        {changes.map((p, index) => (
          <div key={index} className="changed-item-container">
            <span className="changed-item-text">{p}</span>
          </div>
        ))}
      </>
    );
  } else {
    return fallback;
  }
};

const sortItemParameters = (item?: FlowItem): FlowItem | undefined => {
  if (item) {
    return { ...item, parameters: sortBy(item.parameters, 'ref') };
  }
};

export { FlowDiff };
